import { normalizeLeadPhoneNumber } from '@/shared/utils/lead-phone.utils';
export const CAMPAIGN_FORM_STATUS_OPTIONS = [
    { label: 'Draft', value: 'DRAFT' },
    { label: 'Validating', value: 'VALIDATING' },
    { label: 'Ready', value: 'READY' },
    { label: 'Active', value: 'ACTIVE' },
    { label: 'Paused', value: 'PAUSED' },
    { label: 'Ended', value: 'ENDED' },
    { label: 'Archived', value: 'ARCHIVED' },
];
export const CAMPAIGN_STATUS_OPTIONS = [
    { label: 'All Statuses', value: null },
    ...CAMPAIGN_FORM_STATUS_OPTIONS,
];
export const CAMPAIGN_LEAD_STATUS_OPTIONS = [
    { label: 'All Statuses', value: null },
    { label: 'New', value: 'NEW' },
    { label: 'Assigned', value: 'ASSIGNED' },
    { label: 'In Progress', value: 'IN_PROGRESS' },
    { label: 'Called', value: 'CALLED' },
    { label: 'Completed', value: 'COMPLETED' },
    { label: 'Do Not Call', value: 'DO_NOT_CALL' },
    { label: 'Invalid', value: 'INVALID' },
];
export const DEFAULT_CALL_OUTCOMES = [
    'ANSWERED',
    'NO_ANSWER',
    'BUSY',
    'FAILED',
    'INVALID_NUMBER',
    'CALLBACK_REQUESTED',
    'DO_NOT_CALL',
];
export const defaultValue = {
    id: '',
    name: '',
    priority: 5,
    startDate: null,
    endDate: null,
    maxAttemptsPerLead: 3,
    minRetryIntervalMinutes: 30,
    objective: '',
    status: 'DRAFT',
};
export function mapCampaignCustomerToLead(row, campaignId) {
    const agentName = row.assignedAgentName?.trim() || undefined;
    const agentUsername = row.assignedAgentUsername?.trim() || undefined;
    return {
        id: String(row.id),
        campaignId,
        customerName: row.customerName,
        mobileNumber: row.mobileNumber != null ? String(row.mobileNumber) : undefined,
        type: row.customerType,
        service: row.productOrServiceType,
        language: row.preferredLanguage,
        status: row.status,
        attemptCount: row.attemptCount,
        note: row.note ?? undefined,
        lastCalledAt: row.lastCalledAt,
        assignedAgentName: agentName,
        assignedAgentUsername: agentUsername,
        assignedAgentId: agentUsername ?? null,
    };
}
export function mapLeadDtoToLead(dto, campaignId) {
    return {
        id: String(dto.id ?? ''),
        campaignId: campaignId ?? (dto.campaign?.id != null ? String(dto.campaign.id) : undefined),
        customerName: dto.customerName,
        mobileNumber: dto.mobileNumber != null ? String(dto.mobileNumber) : undefined,
        type: dto.customerType,
        service: dto.productOrServiceType,
        language: dto.preferredLanguage,
        status: dto.status,
        attemptCount: dto.attemptCount,
        note: dto.note ?? undefined,
        lastCalledAt: dto.lastCalledAt,
    };
}
export function buildLeadCreateBody(campaignId, values) {
    return {
        campaign: { id: Number(campaignId) },
        customerName: values.name?.trim() || 'Manual Entry',
        mobileNumber: values.number != null && String(values.number).trim()
            ? normalizeLeadPhoneNumber(values.number)
            : '',
        customerType: values.type?.trim() || undefined,
        productOrServiceType: values.service?.trim() || undefined,
        preferredLanguage: values.language?.trim() || undefined,
        status: 'NEW',
        attemptCount: 0,
    };
}
function resolveLeadMobileNumber(values, lead) {
    const fromForm = values.number != null ? String(values.number).trim() : '';
    const fromLead = lead?.mobileNumber != null ? String(lead.mobileNumber).trim() : '';
    const raw = fromForm || fromLead;
    return raw ? normalizeLeadPhoneNumber(raw) : '';
}
/** `PUT /api/leads/{id}` — preserves read-only lead fields from the current row. */
export function buildLeadUpdateBody(lead, campaignId, values) {
    const leadId = Number(lead.id);
    return {
        id: Number.isFinite(leadId) ? leadId : undefined,
        campaign: { id: Number(campaignId) },
        customerName: values.name?.trim() || lead.customerName || 'Manual Entry',
        mobileNumber: resolveLeadMobileNumber(values, lead),
        customerType: values.type?.trim() || lead.type || undefined,
        productOrServiceType: values.service?.trim() || lead.service || undefined,
        preferredLanguage: values.language?.trim() || lead.language || undefined,
        status: lead.status ?? 'NEW',
        attemptCount: lead.attemptCount ?? 0,
        note: lead.note ?? undefined,
        lastCalledAt: lead.lastCalledAt ?? undefined,
    };
}
export function computeAgentLeadStatistics(leads) {
    const statsMap = new Map();
    for (const lead of leads) {
        const agentName = lead.assignedAgentName?.trim() || '-';
        const stat = statsMap.get(agentName) ??
            {
                agentName,
                totalLeads: 0,
                calledLeads: 0,
                uncalledLeads: 0,
                unassignedLeads: 0,
            };
        stat.totalLeads += 1;
        if (lead.lastCalledAt) {
            stat.calledLeads += 1;
        }
        else {
            stat.uncalledLeads += 1;
        }
        if (agentName === '-') {
            stat.unassignedLeads += 1;
        }
        statsMap.set(agentName, stat);
    }
    return [...statsMap.values()].sort((a, b) => {
        if (a.agentName === '-')
            return 1;
        if (b.agentName === '-')
            return -1;
        return a.agentName.localeCompare(b.agentName);
    });
}
